import { useCallback, useEffect, useRef, useState } from "react";

import { faDiagramNested } from "@fortawesome/pro-regular-svg-icons";
import { m } from "framer-motion";

import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Pill } from "src/base-components/Pill";
import { ResizeHandleButton } from "src/base-components/ResizableSidepane";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { PromptBox } from "src/copilot/PromptBox";
import { CopilotSidebarHeader } from "src/flow/analytics/CopilotSidebarHeader";
import { LiveAnalyticsMessages } from "src/flow/analytics/m1/LiveAnalyticsMessages";
import { useLiveAnalyticsChat } from "src/flow/analytics/m1/useLiveAnalyticsChat";
import { useSpecialDomainLabels } from "src/flow/analytics/m1/VisualizeMetrics/useSpecialDomainLabels";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "How is my decision volume progressing over time?",
    prompt: "How is my decision volume progressing over time?",
  },
  {
    label: "Which KPIs should I track to evaluate decision flow performance?",
    prompt: "Which KPIs should I track to evaluate decision flow performance?",
  },
  {
    label: "Show me the decision latency split by P50, P90, Max",
    prompt: "Show me the decision latency split by P50, P90, Max",
  },
];

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

export const CopilotSidebar: React.FC<{
  onClose: () => void;
  flowId: string;
  flowName: string;
}> = ({ onClose, flowId, flowName }) => {
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey: "copilot-sidebar-width",
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: 528,
  });

  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const shouldAutoScrollRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    shouldAutoScrollRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 50;
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el && shouldAutoScrollRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
  }, []);

  const [draft, setDraft] = useState("");
  const {
    isActive,
    isProvisioning,
    isReady,
    isExpired,
    failureMessage,
    messages,
    send,
    resetSession,
  } = useLiveAnalyticsChat({
    flowId,
    flowName,
  });
  const labelDictionaries = useSpecialDomainLabels(flowId, flowName);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, isActive, failureMessage, scrollToBottom]);

  const busy = isActive || !isReady || isExpired;

  const handleSubmit = useCallback(
    (message: string) => {
      if (!message.trim() || busy) return;
      setDraft("");
      send(message);
    },
    [busy, send],
  );

  const handleReset = useCallback(() => {
    setDraft("");
  }, []);

  return (
    <FloatingWindowsProvider>
      <m.div
        animate={{ width }}
        className="shrink-0 overflow-hidden"
        exit={{ width: 0 }}
        initial={{ width: 0 }}
        transition={{ duration: isResizing ? 0 : 0.4, ease: "easeInOut" }}
      >
        <aside
          className="relative flex h-full max-h-screen flex-col border-gray-200 border-l bg-white"
          data-loc="copilot-sidebar"
          style={{ width }}
        >
          <ResizeHandleButton onResizeStart={handleResizeStart} />

          <div className="shrink-0 px-4">
            <CopilotSidebarHeader
              isNewConversation={messages.length === 0 && !failureMessage}
              onClose={onClose}
              onNewConversation={resetSession}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            <ChatBody>
              <ChatMessageList ref={scrollContainerRef}>
                {messages.length === 0 && !isActive && !failureMessage ? (
                  <ChatEmptyState
                    dataLoc="copilot-empty-state"
                    prompts={SUGGESTION_PROMPTS}
                    variant="new"
                    onPromptClick={handleSubmit}
                  />
                ) : (
                  <LiveAnalyticsMessages
                    failureMessage={failureMessage}
                    isActive={isActive}
                    isProvisioning={isProvisioning}
                    labelDictionaries={labelDictionaries}
                    messages={messages}
                  />
                )}
              </ChatMessageList>

              <ChatInputArea>
                <PromptBox
                  disabled={busy || draft.trim().length === 0}
                  errored={false}
                  mentionTextPlaceholder=""
                  minified={false}
                  resource={[]}
                  resourceType={
                    FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
                  }
                  submitLabel={messages.length > 0 ? "Send" : "Start Chat"}
                  topContent={
                    <Pill size="sm" variant="gray">
                      <Pill.Icon icon={faDiagramNested} />
                      <Pill.Text>{flowName}</Pill.Text>
                    </Pill>
                  }
                  value={draft}
                  onChange={setDraft}
                  onReset={handleReset}
                  onSubmit={(content) => handleSubmit(content)}
                />
              </ChatInputArea>
            </ChatBody>
          </div>
        </aside>
      </m.div>
    </FloatingWindowsProvider>
  );
};
